import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { AI_AGENT_BLOCKED_SPACE_IDS } from '@databox/ember-integrations/lib/constants/ai.constants';
import type { HostDataSource } from '@databox/ember-integrations/lib/types/host.types';
import Cookies from 'js-cookie';
import numeral from 'numeral';

import type { APIData, Billing, Coredata, PopupData, Service } from 'webapp-types';
import {
	AccountActivityState,
	AccountNumberFormatType,
	AccountType,
	AppEnv,
	CalendarType,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { copyObject, redirect } from 'webapp/utils/common';

import AccountPopupIntegrationSelectionComponent from 'webapp/components/account/popup-integrations-selection';
import AccountPopupMfaSetupComponent from 'webapp/components/account/popup-mfa-setup';
import CommonPopupDemoLimitationComponent from 'webapp/components/common/popups/demo-limitation';

import type { ReportSlide } from 'webapp/models-custom';
import Board from 'webapp/models-custom/board';
import type { Metadata } from 'webapp/models-custom/space';
import Space from 'webapp/models-custom/space';

import type { Progress as AccountProgressInterface } from 'webapp/services/account-progress';
import type { BillingInfoData } from 'webapp/services/billing';
import { Severity } from 'webapp/services/logger';
import type { SourceInfoForSyncIntervalModal } from 'webapp/services/model-manager/sources';

export const Defaults = {
	COMPANY_NAME: 'Databox',
	COMPANY_LOGO: 'https://cdn1.databox.com/images/logo/databox-default.png',
	APP_LOGO: 'https://cdn1.databox.com/images/logo/databox-default-signonly.svg',
	APP_LOGO_BIG_LIGHT: 'https://cdn1.databox.com/images/logo/databox-black.svg',
	APP_LOGO_BIG_DARK: 'https://cdn1.databox.com/images/logo/databox-default-white.svg',
	DUMMY_LOGO_LIGHT: 'https://cdn1.databox.com/images/logo/dummy-light.svg',
	DUMMY_LOGO_DARK: 'https://cdn1.databox.com/images/logo/dummy-dark.svg',
	DOMAIN: 'app.databox.com',
	SUPPORT_EMAIL: 'help@databox.com',
	HELP_EMAIL: 'help@databox.com',
	HELP_URL: 'https://help.databox.com',
	BOOK_A_CALL_URL: 'https://meetings.hubspot.com/databox-meeting/ref-in-app',
	CONTACT_OWNER_URL:
		'https://meetings.hubspot.com/databox-meeting/customer-onboarding-account-management-meeting-link-post-sale-contact-owner-prioritized-',
	CANCELLATION_MEETING_URL:
		'https://meetings.hubspot.com/databox-cs/customer-success-cancellation-flow-meeting-link',
	RESELLER_PARTNER_URL: 'https://meetings.hubspot.com/databox-meeting/partnerships',
	BOOK_A_CALL_ALTERNATIVE_URL: 'https://meetings.hubspot.com/databox-meeting/ref-in-app-message',
	CONTACT_OWNER_ALTERNATIVE_URL:
		'https://meetings.hubspot.com/databox-meeting/account-management',
};

interface Calendar {
	calendarType: CalendarType;
	calendarConfig?: { fyStartDate: string };
}

type WhitelabelData = Coredata.Whitelabeling;

const WHITE_LABEL_TEXT_KEYS = [
	'companyName',
	'companyLogo',
	'appLogo',
	'requestedDomain',
	'supportEmail',
	'helpUrl',
	'logo',
] as const;

const FAVICON_PATH = 'https://cdn1.databox.com/favicons/';

const getDefaultFaviconPostfix = (env: string): string => {
	if (env === AppEnv.PRODUCTION) {
		return '';
	}

	return env === AppEnv.LOCAL ? '-black' : '-dark';
};

// leaving properties returned from BE in code (commented)
// in case we ever need them again
interface AccountData {
	id: number;
	email: string;
	company: string;
	activityState: AccountActivityState;
	// street: string;
	// city: string;
	// zip: string;
	// state: string;
	country: string;
	// websiteUrl: string;
	invoiceMemo: string;
	timezone: string;
	// taxNumber: string;
	ownerId: number | null;
	parentOwnerId: number | null;
	accountType: AccountType;
	customDomain: string;
	// customEmailDomain: string;
	settings: { dateFormat?: string };
	firstDayOfWeek: number;
	connectedSlack: boolean;
	sslEnabled: boolean;
	onlyAdminsConnect: boolean;
	onlyAdminsCanBuyCredits: boolean;
	numberFormatType: boolean;
	// consents: boolean;
	legacyMetricDeletionDate: string | null; // 'YYYY-MM-DD'
	legacyPushMetricDeletionDate: string | null; // 'YYYY-MM-DD'
}

export interface AccountDataFull extends AccountData {
	billingInfo?: BillingInfoData;
	featureInfo?: Billing.FeatureInfo;
	accountProgress?: AccountProgressInterface;
	whiteLabeling?: WhitelabelData;
	metadata?: Metadata;
	lastProductUpdate?: number;
	removeBrandingOnFeatures?: boolean;
	calendar?: Calendar;
	isSuperTemplatesSpace?: boolean;
	isPublicTemplatesSpace?: true; // only sent when set
}

export default class AccountService extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('automated-popup-queue')
	declare automatedPopupQueueService: Service.AutomatedPopupQueue;
	@service('billing') declare billingService: Service.Billing;
	@service('data-manager') declare dataManagerService: Service.DataManager;
	@service('account-progress') declare accountProgressService: Service.AccountProgress;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('logger') declare loggerService: Service.Logger;
	@service('connect') declare connectService: Service.Connect;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('product-updates') declare productUpdateService: Service.ProductUpdates;
	@service('timeframe') declare timeframeService: Service.Timeframe;

	// set when loading data
	accountServiceUrl!: string;
	cookieAuthSID!: Coredata.CookieAuthSID;

	@tracked isPredefinedBlocksSpace = false;
	@tracked isPublictemplatesSpace = false;

	// account data synced w/ BE
	@tracked id = 0;
	@tracked currentSpace: Space | null = null;
	@tracked activityState = AccountActivityState.ACTIVE;
	@tracked email = '';
	@tracked company = ''; // read-only since AMA ph2
	@tracked country = ''; // used in Forecasts
	@tracked invoiceMemo = '';
	@tracked timezone = '';
	@tracked ownerId: number | null = null;
	@tracked parentOwnerId: number | null = null;
	@tracked accountType = AccountType.REGULAR;
	@tracked customDomain = '';
	@tracked settings: { dateFormat?: string } = {}; // dateFormat read-only since AMA ph2
	@tracked firstDayOfWeek = 7; // read-only since AMA ph2. Mon=1, Sun=0/7 (can be both supposedly. we want to move to 7 eventually which is iso standard)
	@tracked connectedSlack = false; // currently unused, because we can connect slack per user and not only per account
	@tracked sslEnabled = false;
	@tracked onlyAdminsConnect = false;
	@tracked onlyAdminsCanBuyCredits = false;
	@tracked calendar: Calendar = { calendarType: CalendarType.GREGORIAN }; // read-only since AMA ph2
	@tracked numberFormatType = AccountNumberFormatType.AMERICAN;
	@tracked metadata: Metadata = {
		industries: [],
		businessClassifications: [],
		employees: null,
		revenue: null,
		sourcesSelection: [],
		cloudSourcesOpportunity: [], // source type keys from clearbit and/or user input that we "recommend" to connect
		goal: null, // experiment 920261
	};

	// white-labeling
	@tracked companyName = '';
	@tracked companyLogo = '';
	@tracked appLogo = '';
	@tracked requestedDomain = '';
	@tracked removeChatWidget = false;
	@tracked templatesLibraryHiddenForNonAdmins = false;
	@tracked slackIntegrationHiddenForNonAdmins = true;
	@tracked aiAgentHiddenForNonAdmins = false;
	@tracked supportEmail = '';
	@tracked helpUrl = '';
	@tracked logo = '';
	@tracked replaceHelpUrl = false;
	@tracked replaceSupportEmail = false;
	@tracked companyLogoEnabled = false;
	@tracked appLogoEnabled = false;
	@tracked requestedDomainEnabled = false;
	@tracked mobileBrandingEnabled = false;

	@tracked whiteLabelingEnabled = false;
	@tracked whiteLabelingLoaded = false;

	@tracked parentResellerId: number | null = null; // Is paying client
	@tracked accountManagerId: number | null = null;
	@tracked canInviteUsers = false;

	@tracked removeBrandingOnFeatures = false; // key used for controlling Powered By Databox visibility

	private faviconAppLogo: string | null | undefined;

	// when legacy (spreadsheet/SQL, Push API) metrics start being deleted, 'YYYY-MM-DD'; see services/metric-mover.ts
	@tracked legacyMetricDeletionDate: string | null = null;
	@tracked legacyPushMetricDeletionDate: string | null = null;

	get isDomainCustom(): boolean {
		return this.whiteLabelingEnabled && this.requestedDomainEnabled && !!this.customDomain;
	}

	get domain(): string {
		let domain;
		if (this.whiteLabelingEnabled && this.requestedDomainEnabled) {
			domain = this.customDomain;
		}
		return domain || Defaults.DOMAIN;
	}

	get appUrl(): string {
		if (this.customDomain) {
			return 'https://' + this.customDomain;
		}
		return location.origin;
	}

	get shownHelpUrl(): string {
		if (this.replaceHelpUrl && this.helpUrl) {
			return this.helpUrl;
		}
		return Defaults.HELP_URL;
	}

	get shownSupportEmail(): string {
		if (this.replaceSupportEmail && this.supportEmail) {
			return this.supportEmail;
		}
		return Defaults.SUPPORT_EMAIL;
	}

	get shownHelpEmail(): string {
		if (this.replaceSupportEmail && this.supportEmail) {
			return this.supportEmail;
		}
		return Defaults.HELP_EMAIL;
	}

	get shownCompanyName(): string {
		if (this.whiteLabelingEnabled && this.companyName) {
			return this.companyName;
		}
		return Defaults.COMPANY_NAME;
	}

	get isCustomAppLogo() {
		return this.whiteLabelingLoaded && this.appLogoEnabled && this.appLogo;
	}

	get isCustomCompanyLogo() {
		return this.whiteLabelingLoaded && this.companyLogoEnabled && !!this.companyLogo;
	}

	get boardLogo(): string {
		if (this.whiteLabelingLoaded) {
			return this.isCustomAppLogo
				? this.appLogo
				: 'https://cdn1.databox.com/images/logo/databox-signonly-margin.svg';
		} else {
			return Defaults.DUMMY_LOGO_LIGHT;
		}
	}

	get shownAppLogo(): string {
		if (this.whiteLabelingLoaded) {
			return this.isCustomAppLogo ? this.appLogo : Defaults.APP_LOGO;
		} else {
			return Defaults.DUMMY_LOGO_LIGHT;
		}
	}

	get shownCompanyLogo(): string {
		if (this.whiteLabelingLoaded) {
			return this.isCustomCompanyLogo ? this.companyLogo : Defaults.APP_LOGO_BIG_DARK;
		} else {
			return Defaults.DUMMY_LOGO_LIGHT;
		}
	}

	get hideHelpArticles(): boolean {
		return this.whiteLabelingEnabled && this.replaceHelpUrl;
	}

	get showPublicTemplates(): boolean {
		return (
			!this.templatesLibraryHiddenForNonAdmins ||
			!this.hideDataboxNotWhiteLabeledContentForNonAdmins
		);
	}

	get showSlackIntegration(): boolean {
		return (
			!this.slackIntegrationHiddenForNonAdmins ||
			!this.hideDataboxNotWhiteLabeledContentForNonAdmins
		);
	}

	get showAiAgent(): boolean {
		if (AI_AGENT_BLOCKED_SPACE_IDS.includes(this.currentUserService.rootSpaceId ?? -1)) {
			return false;
		}

		return (
			!this.aiAgentHiddenForNonAdmins || !this.hideDataboxNotWhiteLabeledContentForNonAdmins
		);
	}

	get isAdminButNotInClientSpace(): boolean {
		const {
			isAdmin: isCurrentUserAdmin,
			isAdminInRootSpace,
			isAgencyUserInClientSpace,
		} = this.currentUserService;
		let isAdmin = isCurrentUserAdmin && !this.isClient;

		// in client accounts, skip whitelabeling for agency admins
		if (isAgencyUserInClientSpace) {
			isAdmin = isAdminInRootSpace; // `isAdminInRootSpace` can be unreliable, see `rootSpaceLevel` in current user service.
		}

		return isAdmin;
	}

	get hideDataboxNotWhiteLabeledContentForNonAdmins(): boolean {
		return this.whiteLabelingEnabled && !this.isAdminButNotInClientSpace;
	}

	get isActivityStatePaused() {
		return this.activityState === AccountActivityState.PAUSED;
	}

	get accountDateFormatType(): string {
		return this.settings?.dateFormat === 'dd.mm.yyyy' ? 'eu' : 'us'; // default
	}

	// these are moment-supported formats
	get accountDateFormat(): string {
		switch (this.accountDateFormatType) {
			case 'eu':
				return 'DD.MM.YYYY';
			case 'us':
			default:
				return 'MM/DD/YYYY';
		}
	}

	get accountDateFormatWithoutYear(): string {
		switch (this.accountDateFormatType) {
			case 'eu':
				return 'DD.MM';
			case 'us':
			default:
				return 'MM/DD';
		}
	}

	get accountDateFormatHumanReadable(): string {
		switch (this.accountDateFormatType) {
			case 'eu':
				return 'D MMM YYYY';
			case 'us':
			default:
				return 'MMM D, YYYY';
		}
	}

	get accountDateFormatHumanReadableWithoutYear(): string {
		switch (this.accountDateFormatType) {
			case 'eu':
				return 'D MMM';
			case 'us':
			default:
				return 'MMM D';
		}
	}

	get accountTimeFormat(): string {
		switch (this.accountDateFormatType) {
			case 'eu':
				return 'HH:mm';
			case 'us':
			default:
				return 'hh:mm A';
		}
	}

	get accountDatetimeFormat(): string {
		return `${this.accountDateFormat} ${this.accountTimeFormat}`;
	}

	get accountDatetimeFormatHumanReadable(): string {
		return `${this.accountDateFormatHumanReadable} ${this.accountTimeFormat}`;
	}

	get bookACallLink(): string {
		return Defaults.BOOK_A_CALL_URL;
	}

	get bookACallLinkAlternative(): string {
		// exception for metric builder and empty screens
		return Defaults.BOOK_A_CALL_ALTERNATIVE_URL;
	}

	get contactOwnerLink(): string {
		return Defaults.CONTACT_OWNER_URL;
	}

	get contactOwnerLinkAlternative(): string {
		// exception for metric builder and empty screens
		return Defaults.CONTACT_OWNER_ALTERNATIVE_URL;
	}

	get resellerPartnerUrl(): string {
		return Defaults.RESELLER_PARTNER_URL;
	}

	get requestSetupHelpLink(): string {
		if (this.billingService.isFreeOrTrial) {
			return this.bookACallLink;
		} else {
			return this.contactOwnerLink;
		}
	}

	get requestSetupHelpLinkAlternative(): string {
		// exception for metric builder and empty screens
		if (this.billingService.isFreeOrTrial) {
			return this.bookACallLinkAlternative;
		} else {
			return this.contactOwnerLinkAlternative;
		}
	}

	get isNormalSpace(): boolean {
		return this.accountType === AccountType.REGULAR;
	}

	get isAgency(): boolean {
		return this.accountType === AccountType.AGENCY;
	}

	get isClient(): boolean {
		return this.accountType === AccountType.CLIENT;
	}

	get isEligibleForAccountTypeChoice(): boolean {
		return (
			this.isNormalSpace && // agency not chosen yet
			this.currentUserService.isOwner &&
			!this.hasPartnerResellerId &&
			!this.accountService.metadata.employees // user signed up and has already selected account type via onboarding
		); // user nr.1
	}

	get hasCustomCalendar(): boolean {
		return this.calendar.calendarType !== CalendarType.GREGORIAN;
	}

	get accountOwner() {
		const owner = this.mmUsersService.findRecord(String(this.ownerId));
		if (!owner) {
			this.loggerService.logError(
				'Missing account owner',
				{
					ownerId: this.ownerId,
				},
				Severity.WARNING,
			);
		}
		return owner;
	}

	get isEuropeanNumberFormat(): boolean {
		return this.numberFormatType === AccountNumberFormatType.EUROPEAN;
	}

	get currentNumeralLocale() {
		return this.isEuropeanNumberFormat ? 'eu' : 'us';
	}

	get hasPartnerResellerId() {
		return this.parentResellerId !== null && this.parentResellerId > 0;
	}

	openAccountIntegrationsSelectionPopup(props: PopupData.Account.IntegrationSelection): void {
		this.popupService
			.createModal(AccountPopupIntegrationSelectionComponent, {
				popupClass: 'popup-type-account-integrations-selection wizard',
				allowStacked: true,
			})
			.open(props);
	}

	saveSpaceAsAgency(isPortfolio = false) {
		return this.requestService
			.api<APIData.Account.AgencyResponse>('POST', '/authentication/agency', {
				is_portfolio: isPortfolio,
			})
			.then((reply) => {
				if (isPortfolio) {
					this.analyticsService.properties({ is_portfolio: isPortfolio }, [
						'user',
						'company',
					]);
				}
				if (reply?.account) {
					this.update(reply.account);
				}
				const spaceRecord = this.mmSpacesService.savedRecords[0];
				if (spaceRecord) {
					spaceRecord.isEnterprise = true;
				}
			});
	}

	update(accountData: AccountDataFull): void {
		if (accountData.billingInfo) {
			this.billingService.updateBillingInfo(accountData.billingInfo);
			delete accountData.billingInfo;
		}

		if (accountData.featureInfo) {
			this.billingService.updateFeatureInfo(accountData.featureInfo);
			delete accountData.featureInfo;
		}

		if (accountData.metadata) {
			this.updateMetadata(accountData.metadata);
			delete accountData.metadata;
		}

		if (accountData.accountProgress) {
			this.accountProgressService.update(accountData.accountProgress);
			delete accountData.accountProgress;
		}

		if (accountData.lastProductUpdate) {
			this.productUpdateService.lastProductUpdate = accountData.lastProductUpdate;
			delete accountData.lastProductUpdate;
		}

		if (accountData.calendar) {
			const calendarChanged =
				this.calendar.calendarType !== accountData.calendar.calendarType ||
				this.calendar.calendarConfig?.fyStartDate !==
					accountData.calendar.calendarConfig?.fyStartDate;

			if (calendarChanged) {
				this.timeframeService.invalidateTimeframePeriodsCache();
			}

			this.calendar = accountData.calendar;
			delete accountData.calendar;
		}

		const whiteLabeling = accountData.whiteLabeling;
		delete accountData.whiteLabeling;

		Object.assign(this, accountData);

		if (whiteLabeling) {
			this.updateWhitelabelData(whiteLabeling);
		}
		this.whiteLabelingLoaded = true;

		// When updating account we need to update same properties in space data.
		// e.g. updated companyName to be properly shown in topbar-spaces as current
		// space and other spaces
		const currentSpace = this.currentSpace;

		if (currentSpace) {
			currentSpace.directPropUpdate({
				companyName: this.company,
			});
		}

		this.updateActiveNumeralLocale();
	}

	updateMetadata(data: Partial<Metadata>): void {
		// const data={revenue:"$1M-$10M",employees:"11-50",industries:["Technology","Information Technology & Services","Internet"],businessClassifications:["E-commerce","B2B","SAAS","Mobile","B2C"],averageOrderValue:null,targetCustomer:null,productType:null,sellingChannel:null,tech:[],sourcesSelection:[],dataCategories:[],cloudSourcesOpportunity:["CampaignMonitorNew","SendGrid","HubspotCrm","Hubspot","HubspotServices","Mailchimp","Intercom","Twitter","FbAds","GoogleAnalytics","GoogleAnalytics4","Stripe","Salesforce","GitHub","PostgreSQL","MySQL"]};

		// the missing fields are filled in below
		const metadata = copyObject(data) as Metadata;

		if (!data.industries) {
			metadata.industries = [];
		}

		if (!data.businessClassifications) {
			metadata.businessClassifications = [];
		}

		if (!data.employees) {
			metadata.employees = null;
		}

		if (!data.revenue) {
			metadata.revenue = null;
		}

		if (!data.sourcesSelection) {
			metadata.sourcesSelection = [];
		}

		if (!data.cloudSourcesOpportunity) {
			metadata.cloudSourcesOpportunity = [];
		}

		this.metadata = metadata;
	}

	saveMetadataProperties(metadata: { [Property in keyof Metadata]?: Metadata[Property] }) {
		return this.requestService
			.api<APIData.Account.MetadataResponse>('PUT', '/account/metadata', metadata)
			.then((reply) => {
				if (reply) {
					this.updateMetadata(reply);
				}
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	updateWhitelabelData(whitelabelData: Partial<WhitelabelData>): void {
		const cleared = WHITE_LABEL_TEXT_KEYS.filter((key) => !(key in whitelabelData));

		Object.assign(this, Object.fromEntries(cleared.map((key) => [key, ''])), whitelabelData);
		this.whiteLabelingLoaded = true;
		this.updateWhitelabelingEnabledFromBilling();
		this.syncFavicon();
	}

	private syncFavicon(): void {
		const neutralIcon = this.removeBrandingOnFeatures ? `${FAVICON_PATH}empty.png` : null;
		const appLogo = (this.isCustomAppLogo ? this.appLogo : null) || neutralIcon;
		const isFirstLoad = this.faviconAppLogo === undefined;

		if (appLogo === this.faviconAppLogo) {
			return;
		}

		this.faviconAppLogo = appLogo;

		if (isFirstLoad || typeof document === 'undefined') {
			return;
		}

		document.head
			.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon-precomposed"]')
			.forEach((link) => link.remove());

		const postfix = getDefaultFaviconPostfix(window.env);
		const links: { rel: string; href: string; sizes?: string }[] = appLogo
			? [
					{ rel: 'icon', href: appLogo },
					{ rel: 'apple-touch-icon-precomposed', href: appLogo },
				]
			: [
					{
						rel: 'icon',
						href: `${FAVICON_PATH}favicon-32x32${postfix}.png`,
						sizes: '32x32',
					},
					{
						rel: 'icon',
						href: `${FAVICON_PATH}favicon-16x16${postfix}.png`,
						sizes: '16x16',
					},
					{
						rel: 'apple-touch-icon-precomposed',
						href: `${FAVICON_PATH}apple-touch-icon-144x144${postfix}.png`,
						sizes: '144x144',
					},
					{
						rel: 'apple-touch-icon-precomposed',
						href: `${FAVICON_PATH}apple-touch-icon-152x152${postfix}.png`,
						sizes: '152x152',
					},
				];

		links.forEach(({ rel, href, sizes }) => {
			const link = document.createElement('link');
			link.rel = rel;
			link.href = href;
			if (rel === 'icon') {
				link.type = 'image/png';
			}
			if (sizes) {
				link.setAttribute('sizes', sizes);
			}
			document.head.appendChild(link);
		});
	}

	updateWhitelabelingEnabledFromBilling(): void {
		this.whiteLabelingEnabled = !!this.billingService.whiteLabelingEnabled;
	}

	updateAccountDataToBE(data: APIData.Account.AccountUpdate) {
		return this.requestService
			.api<APIData.Account.AccountResponse>('PUT', '/account', { account: data })
			.then((reply) => {
				this.update(reply.account);
				return reply;
			});
	}

	requestSetupHelp(): void {
		window.open(this.requestSetupHelpLink, '_blank');
	}

	requestSetupHelpAlternative(): void {
		window.open(this.requestSetupHelpLinkAlternative, '_blank');
	}

	registerNumeralLocales() {
		// register two locales used for number formatting
		// without ordinal: only the ordinal (o) format reads it
		const abbreviations = {
			thousand: 'k',
			million: 'M',
			billion: 'B',
			trillion: 'T',
		};
		numeral.register('locale', 'eu', {
			abbreviations,
			delimiters: {
				thousands: '.',
				decimal: ',',
			},
			currency: {
				// added currency because numeral uses it in generating value in value() method, we don't use it anywhere
				symbol: '€',
			},
		} as numeral.NumeralJSLocale);
		numeral.register('locale', 'us', {
			abbreviations,
			delimiters: {
				thousands: ',',
				decimal: '.',
			},
			currency: {
				// same as comment above
				symbol: '$',
			},
		} as numeral.NumeralJSLocale);
	}

	updateActiveNumeralLocale() {
		const locale = this.currentNumeralLocale;
		numeral.locale(locale);
	}

	parseAmericanNumber(value: string | number): number | null {
		const currentLocale = this.currentNumeralLocale;
		numeral.locale('us');
		const parsedValue = numeral(value).value();
		numeral.locale(currentLocale);

		return parsedValue;
	}

	getPALoginLink(
		targetEmail = this.currentUserService.model?.email,
		spaceId = this.id,
		customDomain = this.customDomain,
	): string {
		const authServiceUrl = this.requestService.authServiceUrl || 'https://auth-api.databox.com',
			email = encodeURIComponent(targetEmail || ''),
			from = encodeURIComponent(customDomain || location.hostname);
		return `${authServiceUrl}/oauth2/authorize?email=${email}&spaceId=${spaceId}&from=${from}`;
	}

	get accountObject(): APIData.Account.AccountUpdate {
		// for editing or updating account
		return {
			invoiceMemo: this.invoiceMemo,
			onlyAdminsConnect: !!this.onlyAdminsConnect,
			onlyAdminsCanBuyCredits: !!this.onlyAdminsCanBuyCredits,
			metadata: this.metadata,
		};
	}

	openAccountAppProfile() {
		this.openAccountApp('profile');
	}

	openAccountAppCompanyInformation() {
		this.openAccountApp('company-information');
	}

	openAccountAppUsers() {
		this.openAccountApp('user-management');
	}

	openAccountAppEditUser(userId: number) {
		this.openAccountApp(`user-management/users/${userId}/edit-user`);
	}

	openAccountAppEditClientAccess(userId: number) {
		this.openAccountApp(`user-management/users/${userId}/edit-client-access`);
	}

	openAccountAppEditSourceAccess(userId: number) {
		this.openAccountApp(`user-management/users/${userId}/edit-source-access`);
	}

	openAccountAppClients() {
		if (this.handleDemo()) {
			return;
		}
		this.openAccountApp('clients');
	}

	openAccountAppDataManager() {
		this.openAccountApp('data-manager');
	}

	handleSourceDetailsRedirect(
		sId: string,
		datasets?: boolean,
		dataSource?: HostDataSource,
		sourceConnectionInfo?: {
			sourceType: string | number;
			numericId: number;
			syncIntervalData?: SourceInfoForSyncIntervalModal;
		},
	) {
		if (this.handleDemo()) {
			return;
		}

		if (datasets) {
			this.navigationService.transitionToRoute('datamanager.connected.datasets.view', sId);
			return;
		}

		if (this.dataManagerService.functions && dataSource) {
			void this.dataManagerService.functions.openDataSourceDetailsDialog({
				dataSource,
				onDataSourceActionKey: (actionKey: string) => {
					switch (actionKey) {
						case 'timezone':
							this.dataManagerService.changeSourceTimezone(dataSource);
							break;
						case 'purge':
							this.openAccountAppDataManagerPurgeData(sId);
							break;
						case 'permissions':
							this.openAccountAppDataManagerUserPermissions(sId);
							break;
						case 'reauthorize':
							if (sourceConnectionInfo) {
								void this.connectService.connectSourceFromIdOrKey(
									sourceConnectionInfo.sourceType,
									{
										sourceId: sourceConnectionInfo.numericId,
										reauthorize: true,
										origin: 'connectedRow',
									},
								);
							}
							break;
						case 'reconnect':
						case 'source_connected':
							if (sourceConnectionInfo) {
								void this.connectService.connectSourceFromIdOrKey(
									sourceConnectionInfo.sourceType,
									{
										sourceId: sourceConnectionInfo.numericId,
										reauthorize: false,
										origin: 'connectedRow',
									},
								);
							}
							break;
						case 'updateFrequency':
							if (sourceConnectionInfo?.syncIntervalData) {
								this.mmSourcesService.openSyncIntervalModal(
									sourceConnectionInfo.syncIntervalData,
									'data_manager',
								);
							}
							break;
					}
				},
			});
		}
	}

	openAccountAppDataManagerPurgeData(sId: string) {
		if (this.handleDemo()) {
			return;
		}
		this.openAccountApp(`data-manager/${sId}/purge-data`);
	}

	openAccountAppDataManagerUserPermissions(sId: string) {
		if (this.handleDemo()) {
			return;
		}
		this.openAccountApp(`data-manager/${sId}/user-permissions`);
	}

	openAccountAppClientManagerEdit(spaceId: number, enablePaidClient = false) {
		if (this.handleDemo()) {
			return;
		}
		this.openAccountApp(`clients/${spaceId}/edit`, false, enablePaidClient);
	}

	openAccountAppClientManagerEditGeneral(spaceId: number) {
		if (this.handleDemo()) {
			return;
		}
		this.openAccountApp(`clients/${spaceId}/edit-general`);
	}

	openAccountAppSso() {
		this.openAccountApp('single-sign-on');
	}

	openAccountAppWhitelabeling() {
		this.openAccountApp('whitelabeling');
	}

	redirectToAccountAppProfile(isRedirect = true, locationHash = '') {
		this.openAccountApp('profile', isRedirect, false, locationHash);
	}

	getAccountAppUrl(): string {
		return this.isDomainCustom ? `${this.appUrl}/accounts` : this.accountServiceUrl;
	}

	private openAccountApp(
		pathname = '',
		isRedirect = false,
		enablePaidClient = false,
		locationHash = '',
	): void {
		let url = this.getAccountAppUrl();
		if (pathname) {
			url += `/${pathname}?spaceId=${this.id}`;

			if (enablePaidClient) {
				url += `&enablePaidClient=true`;
			}
		}

		if (locationHash) {
			url += `#${locationHash}`;
		}

		if (isRedirect) {
			location.assign(url);
		} else {
			window.open(url, '_blank');
		}
	}

	get isDemoAccount(): boolean {
		return this.currentUserService.isDemo;
	}

	get canAddClient(): boolean {
		return this.billingService.isAgencyAdmin && !this.isDemoAccount;
	}

	get isExistingUserAccount(): boolean {
		return this.currentUserService.isExistingUser;
	}

	get isExistingUserEmailSet(): boolean {
		return this.currentUserService.isExistingUserEmailSet;
	}

	get isDemoButtonShown(): boolean {
		return (
			!this.isDemoAccount &&
			!this.hideDataboxNotWhiteLabeledContentForNonAdmins &&
			!this.billingService.isActive
		);
	}

	private createDemoLimitationPopup() {
		return this.popupService.createModal(CommonPopupDemoLimitationComponent, {
			popupClass: 'popup-type-demo-limitation',
		});
	}

	handleDemo(): boolean {
		const isDemo = this.isDemoAccount;
		if (isDemo) {
			this.createDemoLimitationPopup().open();
		}

		return isDemo;
	}

	removeSessionIDCookie() {
		const { name, domain } = this.cookieAuthSID;
		Cookies.remove(name, { domain });
	}

	redirectToDemoAccount(trackingOrigin: string) {
		this.analyticsService.event(TrackingEvent.DEMO_OPENED, { origin: trackingOrigin });
		return this.requestService
			.authService<APIData.Auth.DemoAccountRedirectResponse>(
				'GET',
				`/demo-account/login-with-user/${location.host}`,
			)
			.then((reply) => {
				const redirectUrl = reply.redirectUrl;
				if (redirectUrl) {
					this.removeSessionIDCookie();
					redirect(redirectUrl);
				}
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	redirectToMainAccount(trackingType: string) {
		const eventData = {
			origin: this.navigationService.getNormalizedTrackingNameForPath(),
			type: trackingType,
		};

		this.analyticsService.event(TrackingEvent.DEMO_BACK_TO_ANALYTICS, eventData);

		return this.requestService
			.authService<APIData.Auth.DemoAccountRedirectResponse>(
				'GET',
				`/demo-account/switch-back/${location.host}`,
			)
			.then((reply) => {
				const redirectUrl = reply.redirectUrl;
				if (redirectUrl) {
					this.removeSessionIDCookie();
					redirect(redirectUrl);
				}
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	redirectToLogin(trackingType: string) {
		const eventData = {
			origin: this.navigationService.getNormalizedTrackingNameForPath(),
			type: trackingType,
		};

		this.analyticsService.event(TrackingEvent.DEMO_LOGIN, eventData);
		this.navigationService.logout();
	}

	redirectToSignup(trackingType: string) {
		const eventData = {
			origin: this.navigationService.getNormalizedTrackingNameForPath(),
			type: trackingType,
		};

		this.analyticsService.event(TrackingEvent.DEMO_CREATE_ACCOUNT, eventData);

		const refererUrl = encodeURIComponent(window.location.href);
		redirect(
			`${this.requestService.websiteUrl}/signup?origin=analytics-demo&refererUrl=${refererUrl}`,
		);
	}

	// ------ Global default visual settings ------

	globalVisualSettingsForBoard: APIData.GlobalVisualSettings.VisualSettings = {};
	globalVisualSettingsForReportSlide: APIData.GlobalVisualSettings.VisualSettings = {};

	requestGlobalVisualSettings() {
		return this.requestService
			.api<APIData.GlobalVisualSettings.Response>('GET', '/settings/global')
			.then((response) => {
				this.updateGlobalSettings(response);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	saveGlobalSettings(
		model: Board | ReportSlide,
		requestSettings: {
			overrideExisting: boolean;
			defaultForClients: boolean;
			overrideExistingForClients: boolean;
		},
	) {
		const settings: APIData.GlobalVisualSettings.AdditionalSettings = {};

		type ModelProperty = keyof (Board | ReportSlide);
		const propertyGroups: Record<string, ModelProperty[]> = {
			bg: ['bg', 'bgOriginal', 'bgCrop', 'bgOpacity'] as ModelProperty[],
			logo: ['logo', 'logoOriginal', 'logoCrop'] as ModelProperty[],
		};

		// First, get all properties that should be included based on their group dependencies
		const propertiesToInclude = Object.entries(propertyGroups).reduce(
			(acc, [mainProp, groupProps]) => {
				if (model[mainProp as ModelProperty]) {
					// If main property exists, include all properties from the group that have values
					groupProps.forEach((prop) => {
						if (model[prop] !== undefined) {
							acc.push(prop);
						}
					});
				}
				return acc;
			},
			[] as ModelProperty[],
		);

		Object.assign(
			settings,
			Object.fromEntries(propertiesToInclude.map((prop) => [prop, model[prop]])),
		);

		const visualSettings: APIData.GlobalVisualSettings.VisualSettings = {
			color: model.color,
			themeId: model.themeId,
		};

		// Only include settings if there are any
		if (Object.keys(settings).length > 0) {
			visualSettings.settings = settings;
		}

		let requestPath = 'report';
		if (model instanceof Board) {
			requestPath = 'board';
		}

		const payload: APIData.GlobalVisualSettings.PostRequest = {
			...requestSettings,
			visual: visualSettings,
		};

		return this.requestService.api('POST', `/settings/global/${requestPath}`, payload);
	}

	updateGlobalSettings(settings: APIData.GlobalVisualSettings.Response) {
		if (settings.board) {
			this.globalVisualSettingsForBoard = settings.board;
		}
		if (settings.reportSlide) {
			this.globalVisualSettingsForReportSlide = settings.reportSlide;
		}
	}

	// ------ MFA ------

	private createMfaSetupPopup() {
		return this.popupService.createModal(AccountPopupMfaSetupComponent, {
			popupClass: 'popup-mfa-setup',
			showClose: false,
			closeOnOverlayClick: false,
		});
	}

	checkToAddMfaSetupPopup(isMfaRequired: boolean) {
		const popup = this.createMfaSetupPopup();

		this.automatedPopupQueueService.queuePopup({
			popup,
			routeBlacklist: ['*'],
			checkAddConditions: () => {
				return isMfaRequired;
			},
			checkStartConditions: () => {
				return isMfaRequired;
			},
			skipCooldown: true,
		});
	}

	trackSignup() {
		// for bing ads (UET)
		window.uetq = window.uetq || ([] as (string | Record<string, unknown> | undefined)[]); // the queue UET reads on load
		window.uetq?.push('event', 'signup_bing', {});
	}
}

declare module '@ember/service' {
	interface Registry {
		account: AccountService;
	}
}
